import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useProfile } from '../../hooks/useProfile';
import { Loader2, User, Hash, Eye, Lock, Info } from 'lucide-react';
import { Breadcrumbs } from '../../components/navigation/Breadcrumbs';
import { UsernameInput } from '../../components/auth/UsernameInput';
import { FriendIdInput } from '../../components/auth/FriendIdInput';
import { FriendIdTooltip } from '../../components/auth/FriendIdTooltip';
import { useUsernameAvailability } from '../../hooks/useUsernameAvailability';
import { AvatarUpload } from '../../components/profile/AvatarUpload';
import { PageTitle } from '../../components/layout/PageTitle';
import { ChangePasswordForm } from '../../components/profile/ChangePasswordForm';
import { PageLayout } from '../../components/layout/PageLayout';
import { useTranslation } from '../../hooks/useTranslation';
import { useI18n } from '../../contexts/I18nContext';

export const Profile = () => {
  const { t } = useTranslation();
  const { currentLang } = useI18n();
  const { user } = useAuth();
  const { profile, loading, error: profileError, updateProfile } = useProfile();
  const [showTooltip, setShowTooltip] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [username, setUsername] = useState('');
  const [friendId, setFriendId] = useState('');
  const [isPokemonIdPublic, setIsPokemonIdPublic] = useState(false);
  const [isCollectionPublic, setIsCollectionPublic] = useState(false);
  const [pokemonIdDescription, setPokemonIdDescription] = useState('');
  const [descriptionCharCount, setDescriptionCharCount] = useState(0);
  const MAX_DESCRIPTION_LENGTH = 200;

  const hasChanges = 
    username !== profile?.username ||
    friendId !== profile?.pokemon_id ||
    isPokemonIdPublic !== profile?.is_pokemon_id_public ||
    isCollectionPublic !== profile?.is_collection_public ||
    pokemonIdDescription !== profile?.pokemon_id_description;

  const { isChecking, isAvailable, error: usernameError } = useUsernameAvailability(
    username !== profile?.username ? username : ''
  );

  useEffect(() => {
    if (profile) {
      setUsername(profile.username || '');
      setFriendId(profile.pokemon_id || '');
      setIsPokemonIdPublic(profile.is_pokemon_id_public || false);
      setIsCollectionPublic(profile.is_collection_public || false);
      setPokemonIdDescription(profile.pokemon_id_description || '');
      setDescriptionCharCount(profile.pokemon_id_description?.length || 0);
    }
  }, [profile]);

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= MAX_DESCRIPTION_LENGTH) {
      setPokemonIdDescription(text);
      setDescriptionCharCount(text.length);
    }
  };

  const handleAvatarUpload = async (url: string) => {
    try {
      await updateProfile({ avatar_url: url });
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Erreur lors de la mise à jour de l\'avatar');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || isUpdating) return;

    // Vérifier la disponibilité du nom d'utilisateur
    if (username !== profile?.username && !isAvailable) {
      setUpdateError('Ce nom d\'utilisateur n\'est pas disponible');
      return;
    }

    setIsUpdating(true);
    setUpdateError(null);

    try {
      await updateProfile({
        username: username.trim(),
        pokemon_id: friendId.trim() || null,
        is_pokemon_id_public: isPokemonIdPublic,
        is_collection_public: isCollectionPublic,
        pokemon_id_description: pokemonIdDescription
      });
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading || !profile) {
    return (
      <PageLayout disableAds={true}>
        <div className="flex justify-center items-center min-h-[calc(100vh-4rem)]">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      </PageLayout>
    );
  }

  if (profileError) {
    return (
      <PageLayout disableAds={true}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <p className="text-red-500">{t('profile.error')}</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout disableAds={true}>
      <div className="container mx-auto px-4 py-8">
        <PageTitle title={t('profile.title')} />
        <div className="min-h-screen bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Breadcrumbs items={[{ label: t('profile.title'), path: '/profile' }]} />
            
            {/* Banner and Avatar Section */}
            <div className="mt-8">
              <div className="relative">
                <div className="h-48 w-full bg-gradient-to-r from-primary-600 to-primary-800 rounded-t-2xl" />
                <div className="absolute -bottom-16 left-8">
                  <AvatarUpload
                    url={profile.avatar_url}
                    onUpload={handleAvatarUpload}
                    size={128}
                    className="ring-4 ring-white rounded-full shadow-lg"
                  />
                </div>
              </div>

              {/* User Info Header */}
              <div className="bg-white rounded-b-2xl shadow-sm">
                <div className="pt-20 pb-6 px-8">
                  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900">{username || 'Utilisateur'}</h1>
                      <div className="mt-1 flex items-center gap-2 text-gray-500">
                        <Hash className="w-4 h-4" />
                        <span>{user?.email}</span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {t('profile.member_since')} {new Date(user?.created_at || '').toLocaleDateString(currentLang === 'en' ? 'en-US' : 'fr-FR', {
                        year: 'numeric',
                        month: 'long'
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Form */}
            <form onSubmit={handleSubmit} className="mt-8 space-y-6">
              {/* Profile Section */}
              <section className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="border-b border-gray-100">
                  <div className="px-8 py-6">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <User className="w-5 h-5 text-primary-500" />
                      {t('profile.profile_info.title')}
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                      {t('profile.profile_info.subtitle')}
                    </p>
                  </div>
                </div>

                <div className="p-8 space-y-8">
                  {/* Username subsection */}
                  <div className="space-y-6">
                    <UsernameInput
                      username={username}
                      onChange={setUsername}
                      isChecking={isChecking}
                      isAvailable={username === profile.username || isAvailable}
                      error={usernameError || undefined}
                    />
                  </div>

                  {/* Friend Code subsection */}
                  <div className="pt-6 border-t border-gray-100">
                    <div className="space-y-4">
                      <FriendIdInput
                        value={friendId}
                        onChange={setFriendId}
                        onShowTooltip={() => setShowTooltip(!showTooltip)}
                      />
                      {showTooltip && <FriendIdTooltip onClose={() => setShowTooltip(false)} />}
                      
                      {/* Description du Code Ami */}
                      {friendId && (
                        <div className="mt-4 space-y-2">
                          <div className="flex justify-between items-end">
                            <label htmlFor="pokemonIdDescription" className="block text-sm font-medium text-gray-700">
                              {t('profile.friend_code.description_label')}
                            </label>
                            <span className={`text-xs ${descriptionCharCount >= MAX_DESCRIPTION_LENGTH ? 'text-red-500' : 'text-gray-500'}`}>
                              {descriptionCharCount}/{MAX_DESCRIPTION_LENGTH}
                            </span>
                          </div>
                          <textarea
                            id="pokemonIdDescription"
                            rows={3}
                            placeholder={t('profile.friend_code.description_placeholder')}
                            value={pokemonIdDescription}
                            onChange={handleDescriptionChange}
                            maxLength={MAX_DESCRIPTION_LENGTH}
                            className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm resize-none"
                          />
                          <div className="rounded-lg bg-blue-50 p-3 border border-blue-100 flex items-start gap-2">
                            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-700">
                              {t('profile.friend_code.info_tip')}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              {/* Privacy Section */}
              <section className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="border-b border-gray-100">
                  <div className="px-8 py-6">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Eye className="w-5 h-5 text-primary-500" />
                      {t('profile.privacy.title')}
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                      {t('profile.privacy.subtitle')}
                    </p>
                  </div>
                </div>

                <div className="p-8 space-y-6">
                  {/* Collection Visibility */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex-1">
                      <span className="block font-medium text-gray-900 text-sm">{t('profile.privacy.collection_visibility')}</span>
                      <p className="text-gray-500 text-sm">{t('profile.privacy.collection_description')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={isCollectionPublic}
                        onChange={(e) => setIsCollectionPublic(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  {/* Friend Code Visibility */}
                  {friendId && (
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <div className="flex-1">
                        <span className="block font-medium text-gray-900 text-sm">{t('profile.privacy.friend_code_visibility')}</span>
                        <p className="text-gray-500 text-sm">{t('profile.privacy.friend_code_description')}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-4">
                        <input
                          type="checkbox"
                          checked={isPokemonIdPublic}
                          onChange={(e) => setIsPokemonIdPublic(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                      </label>
                    </div>
                  )}
                </div>
              </section>

              {/* Error Messages */}
              {updateError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-sm text-red-600">{updateError}</p>
                </div>
              )}

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isUpdating || !hasChanges || isChecking || (username !== profile.username && !isAvailable)}
                  className="inline-flex items-center px-8 py-3 border border-transparent rounded-xl text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  {isUpdating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t('profile.buttons.updating')}
                    </>
                  ) : (
                    t('profile.buttons.save')
                  )}
                </button>
              </div>
            </form>

            {/* Password Section - Moved outside the main form */}
            <section className="mt-8 bg-white rounded-2xl shadow-sm overflow-hidden">
              <div className="border-b border-gray-100">
                <div className="px-8 py-6">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-primary-500" />
                    {t('profile.security.title')}
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    {t('profile.security.subtitle')}
                  </p>
                </div>
              </div>
              <div className="p-8">
                <ChangePasswordForm />
              </div>
            </section>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}